import { useEffect, useMemo, useState } from "react";
import { apiFetch, apiDelete, apiPut } from "../api/api";
import { Link } from "react-router-dom";
import { Trash2, Pencil } from "lucide-react";

export default function IbuList() {
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [form, setForm] = useState<any>({});
  const [loadingEdit, setLoadingEdit] = useState(false);

  const pageSize = 10;

  /* =====================
     LOAD DATA
  ===================== */
  const loadData = () => {
    apiFetch("/admin/users").then(res => setRows(res.data));
  };

  useEffect(() => {
    loadData();
  }, []);

  /* =====================
     SEARCH (SAFE)
  ===================== */
  const filtered = useMemo(() => {
    return rows.filter(r =>
      (r.nama ?? "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [rows, search]);

  /* =====================
     PAGINATION
  ===================== */
  const totalPage = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pagedData = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  /* =====================
     DELETE
  ===================== */
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoadingDelete(true);
      await apiDelete(`/admin/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoadingDelete(false);
    }
  };

  /* =====================
     OPEN EDIT
  ===================== */
  const openEdit = (row: any) => {
    setEditTarget(row);
    setForm({
      nama: row.nama ?? "",
      email: row.email ?? "",
      usia: row.usia ?? "",
      usia_kehamilan_awal: row.usia_kehamilan_awal ?? "",
      alamat: row.alamat ?? "",
      password: ""
    });
  };

  /* =====================
     SUBMIT EDIT
  ===================== */
  const submitEdit = async () => {
    if (!form.nama || !form.email) {
      alert("Nama dan email wajib diisi");
      return;
    }

    try {
      setLoadingEdit(true);

      const payload: any = {
        nama: form.nama,
        email: form.email,
        usia: form.usia,
        usia_kehamilan_awal: form.usia_kehamilan_awal,
        alamat: form.alamat
      };

      if (form.password && form.password.trim() !== "") {
        payload.password = form.password;
      }

      await apiPut(`/admin/users/${editTarget.id}`, payload);

      setEditTarget(null);
      loadData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoadingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="bg-white rounded-2xl shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-purple-700">
              Data Ibu
            </h1>
            <p className="text-sm text-gray-500">
              Daftar ibu hamil terdaftar di sistem
            </p>
          </div>

          <input
            type="text"
            placeholder="Cari nama ibu..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border rounded-lg px-4 py-2 w-full md:w-64
                       focus:ring-2 focus:ring-purple-400"
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <tr>
              <th className="px-5 py-3 text-left">Nama</th>
              <th className="px-5 py-3 text-left">Email</th>
              <th className="px-5 py-3 text-left">Usia</th>
              <th className="px-5 py-3 text-center">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {pagedData.map((r, i) => (
              <tr
                key={r.id}
                className={`border-b hover:bg-purple-50 transition
                  ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
              >
                <td className="px-5 py-3 font-medium text-gray-700">
                  {r.nama}
                </td>
                <td className="px-5 py-3 text-gray-600">
                  {r.email}
                </td>
                <td className="px-5 py-3 text-gray-600">
                  {r.usia ?? "-"}
                </td>
                <td className="px-5 py-3 text-center space-x-3">
                  <Link
                    to={`/ibu/${r.id}`}
                    className="text-purple-600 font-semibold hover:underline"
                  >
                    Detail
                  </Link>

                  <button
                    onClick={() => openEdit(r)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit data"
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    onClick={() => setDeleteTarget(r)}
                    className="text-red-600 hover:text-red-800"
                    title="Hapus akun"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}

            {pagedData.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-10 text-gray-500"
                >
                  Data tidak ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {filtered.length > 0 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Menampilkan {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, filtered.length)} dari {filtered.length} data
          </p>

          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 rounded-lg border text-sm
                         disabled:opacity-40 hover:bg-purple-50"
            >
              Prev
            </button>

            <span className="px-3 py-2 text-sm font-semibold">
              {page} / {totalPage}
            </span>

            <button
              disabled={page === totalPage}
              onClick={() => setPage(p => p + 1)}
              className="px-4 py-2 rounded-lg border text-sm
                         disabled:opacity-40 hover:bg-purple-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-bold text-purple-700">
              Edit Data Ibu
            </h3>

            {["nama", "email", "usia", "usia_kehamilan_awal", "alamat", "password"].map(
              field => (
                <input
                  key={field}
                  type={field === "password" ? "password" : "text"}
                  placeholder={
                    field === "password"
                      ? "Password baru (opsional)"
                      : field.replaceAll("_", " ")
                  }
                  value={form[field]}
                  onChange={e =>
                    setForm({ ...form, [field]: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2"
                />
              )
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setEditTarget(null)}
                className="px-4 py-2 border rounded-lg"
              >
                Batal
              </button>

              <button
                onClick={submitEdit}
                disabled={loadingEdit}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg
                           disabled:opacity-50"
              >
                {loadingEdit ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-red-600">
              Hapus Akun Ibu
            </h3>

            <p className="mt-3 text-gray-600">
              Yakin ingin menghapus akun:
            </p>

            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-semibold">{deleteTarget.nama}</p>
              <p className="text-gray-500">{deleteTarget.email}</p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border rounded-lg"
              >
                Batal
              </button>

              <button
                onClick={handleDelete}
                disabled={loadingDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg
                           disabled:opacity-50"
              >
                {loadingDelete ? "Menghapus..." : "Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
